import {ReportConfiguration, ReportListElement} from '../../models/reports.model';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {ReportsActions} from './reports.actions';
import {ReportsService} from '../../services/reports.service';
import {catchError, map, tap} from 'rxjs/operators';
import {SearchList} from '../../models/search-list.model';
import {FormState} from '../../models/form-state.model';
import {handleFilters} from '../../helpers/search.helper';
import {FileService} from '../../services/file.service';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {EMPTY} from 'rxjs';
import {NotificationService} from '../../services/notification.service';
import {ConfirmationModalComponent} from '../../modules/shared/components/confirmation-modal';

interface ReportsStateModel {
    reportsItems: ReportListElement[];
    configuration: ReportConfiguration;
    list: SearchList<{}>;
    activeReportCode: string;
}

@State<ReportsStateModel>({
    name: ReportsState.STATE_NAME,
    defaults: {
        reportsItems: [],
        configuration: null,
        list: new SearchList<{}>({
            filters: new FormState(),
        }),
        activeReportCode: null
    }
})

@Injectable()
export class ReportsState {

    static readonly STATE_NAME = 'reports';

    constructor(
        private reportsService: ReportsService,
        private fileService: FileService,
        private modalService: ModalService,
        private translateService: TranslateService,
        private notificationService: NotificationService,
    ) {
    }

    @Selector()
    static reportsItems(state) {
        return state.reportsItems;
    }

    @Selector()
    static reportConfiguration(state) {
        return state.configuration;
    }

    @Selector()
    static activeReport(state) {
        return state.reportsItems?.find(report => report.code === state.activeReportCode);
    }

    @Action(ReportsActions.GetReportsItems)
    getReportsList(ctx: StateContext<ReportsStateModel>) {
        return this.reportsService.getReports().pipe(
            tap((reportsItems: ReportListElement[]) => {
                ctx.patchState({
                    reportsItems
                });
            })
        );
    }

    @Action(ReportsActions.GetReport)
    getReport(ctx: StateContext<ReportsStateModel>, {activeReportCode}: ReportsActions.GetReport) {
        return this.reportsService.getReportConfiguration(activeReportCode).pipe(
            tap((configuration: ReportConfiguration) => {
                ctx.patchState({
                    configuration,
                    activeReportCode
                });
            })
        );
    }

    @Action(ReportsActions.GenerateFilteredReport)
    generateFilteredReport(ctx: StateContext<ReportsStateModel>, action: ReportsActions.GenerateFilteredReport) {
        return handleFilters(ctx, action.params,
            (props) => this.reportsService.generateReport(ctx.getState().activeReportCode, props))
            .pipe(
                map(() => ctx.dispatch(new ReportsActions.GenerateFilteredReportSuccess())),
                catchError(({error}) => {
                    const nullErrors = error.filter((singleError) => singleError.field === null);
                    if (Array.isArray(nullErrors) && nullErrors.length) {
                        this.notificationService.error(nullErrors[0].description);
                    }
                    return EMPTY;
                })
            );
    }

    @Action(ReportsActions.ClearReportData)
    clearReportData(ctx: StateContext<ReportsStateModel>) {
        ctx.patchState({
            configuration: null,
            activeReportCode: null,
            list: new SearchList<{}>({
                filters: new FormState(),
            }),
        });
    }

    @Action(ReportsActions.GenerateFilteredReportSuccess)
    generateFilteredReportSuccess() {
        this.modalService.generic({
            nzContent: ConfirmationModalComponent,
            nzFooter: [
                {
                    label: this.translateService.instant('LABELS.CLOSE'),
                    type: 'primary',
                    onClick: (component: ConfirmationModalComponent) => {
                        component.close();
                    },
                }
            ],
            nzKeyboard: false,
            nzClosable: false,
            nzMaskClosable: false,
            nzComponentParams: {confirmationText: this.translateService.instant('REPORTS.CONFIRMATION')},
        });
    }
}
